import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core'
import { AbstractControl } from '@angular/forms'
import { Subscription } from 'rxjs'
import { ERROR_GLYPHS } from 'src/utils/presented-error'

/**
 * Tier 1 of the error system: the input that caused the failure owns the message. The one
 * field-error primitive in the app — it renders under its control and clears on edit.
 *
 * Takes a `message` for local validator copy, and/or a `control` so a backend validation
 * failure claimed by `FieldErrorHostDirective` surfaces on the same input. A local message
 * wins: the user's most recent edit is more relevant than the last server round-trip.
 */
@Component({
  selector: 'arp-field-issue',
  templateUrl: './field-issue.component.html',
  styleUrls: ['./field-issue.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldIssueComponent implements OnDestroy {
  /** Copy from a local validator. Falsy renders nothing. */
  @Input() message: string | null = null

  readonly glyph = ERROR_GLYPHS.failure

  private _control: AbstractControl | null = null
  private statusSubscription: Subscription = null

  /**
   * Read for a `server` error stamped by `FieldErrorHostDirective`. Setting errors on a
   * control doesn't change its identity, so OnPush would never re-check — the status
   * subscription is what makes a claimed error appear.
   */
  @Input()
  set control(value: AbstractControl | null) {
    if (value === this._control) return

    this.statusSubscription?.unsubscribe()
    this._control = value
    this.statusSubscription = value?.statusChanges.subscribe(() => this.cdr.markForCheck())
  }

  get control(): AbstractControl | null {
    return this._control
  }

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnDestroy(): void {
    this.statusSubscription?.unsubscribe()
  }

  get text(): string | null {
    if (this.message) return this.message
    const server = this._control?.errors?.['server']
    return typeof server === 'string' ? server : (server?.message ?? null)
  }
}
